/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useQuery} from '@tanstack/react-query'
import ajax from 'helpers/ajax'
import {DEFAULT_HALL_ID} from 'helpers/config'
import {ReservationsTableRequest} from 'types/requests/ReservationsTableRequest'
import {useAppDispatch} from 'helpers/store'
import ReservationsAction from 'store/reservations/ReservationsAction'
import {useEffect, useMemo, useRef} from 'react'
import OnlineReservationsAction from "store/reservations/OnlineReservationsAction"

interface UseReservationsQueryOptions {
    endpoint?: string
    includeSellerId?: boolean
    includeSearch?: boolean
    syncToRedux?: boolean,
	isOnlineReservations?: boolean,
    tableStateParams?: ReservationsTableRequest,
    forceNetworkRefetch?: boolean,
}

export const useReservationsQuery = (
    sellerId: number | null,
    params: ReservationsTableRequest,
    enabled: boolean = true,
    options: UseReservationsQueryOptions = {}
) => {
    const dispatch = useAppDispatch()
    const endpoint = options.endpoint || '/online-venue/reservation/seller/pagination'
    const includeSellerId = options.includeSellerId ?? true
    const includeSearch = options.includeSearch ?? true
    const syncToRedux = options.syncToRedux ?? true
    const forceNetworkRefetch = options.forceNetworkRefetch ?? true

    const queryKey = useMemo(
        () => [
            'reservations',
            endpoint,
            includeSellerId,
            includeSearch,
            syncToRedux,
            sellerId,
            params.page,
            params.per_page,
            JSON.stringify(params.search ?? []),
            JSON.stringify(params.sorting ?? []),
            JSON.stringify(params.filter ?? null),
        ],
        [
            endpoint,
            includeSellerId,
            includeSearch,
            syncToRedux,
            sellerId,
            params.page,
            params.per_page,
            params.search,
            params.sorting,
            params.filter,
        ]
    )

    const query = useQuery({
        queryKey,
        queryFn: async () => {
            if (includeSellerId && !sellerId) throw new Error('Seller ID is required')

            const payload: Record<string, unknown> = {
                hall_id: DEFAULT_HALL_ID,
                per_page: params.per_page,
                page: params.page,
                sorting: params.sorting,
            }

            if (params.filter !== undefined) {
                payload.filter = params.filter
            }

            if (includeSearch && params.search !== undefined) {
                payload.search = params.search
            }

            if (includeSellerId) {
                payload.seller_id = sellerId
            }

            const response = await ajax.post(
                endpoint,
                payload
            )

            return response.reservations
        },
        enabled: enabled && (includeSellerId ? !!sellerId : true),
        staleTime: forceNetworkRefetch ? 0 : undefined,
        refetchOnMount: forceNetworkRefetch ? 'always' : undefined,
    })

    const isLoading = query.isLoading || query.isFetching

    const tableStateParamsRef = useRef(options.tableStateParams)
    tableStateParamsRef.current = options.tableStateParams

    // Sync with Redux for backward compatibility
    useEffect(() => {
        if (!query.data || !syncToRedux) {
            return
        }

        const syncedTableStateParams = tableStateParamsRef.current ?? params

        if (options.isOnlineReservations) {
            dispatch(OnlineReservationsAction.setReservations(query.data.data))
            dispatch(OnlineReservationsAction.setTableState(syncedTableStateParams))
            return
        }

        dispatch(ReservationsAction.setReservations(query.data.data))
        dispatch(ReservationsAction.setTableState(syncedTableStateParams))
        // Intentionally keyed to query updates to avoid dispatch loops
        // from unstable params object references.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.dataUpdatedAt, query.data, syncToRedux, options.isOnlineReservations])

    return {
        ...query,
        isLoading,
    }
}

